import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { DesktopDateRangePicker, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/it';
import React from 'react';
import { normalizeDate } from './UniversalDatePicker';

export interface UniversalDateRangePickerProps {
  /** Identifier per la gestione dei form standard */
  name?: string;
  /** Valore corrente in modalità controllata: tupla di stringhe ISO [start, end] o null */
  value?: [string | null, string | null] | null;
  /** Callback attivata al cambio di range, restituisce le stringhe 'YYYY-MM-DD' o null */
  onChange?: (value: [string | null, string | null] | null) => void;
  /** Iniezione automatica dello stato per React Final Form */
  input?: {
    name: string;
    value: [string | null, string | null] | null;
    onChange: (value: [string | null, string | null] | null) => void;
  };
  /** Stato di validazione iniettato da React Final Form */
  meta?: {
    touched?: boolean;
    error?: string;
  };
  /** Testo segnaposto interno al campo */
  placeholder?: string;
  /** Disabilita interamente il selettore */
  disabled?: boolean;
  /** Impedisce la selezione di date future */
  noFuture?: boolean;
  /** Nasconde l'icona del calendario per l'apertura del popup */
  disableOpenPicker?: boolean;
  /** Larghezza minima del componente */
  minWidth?: number | string;
  /** Callback opzionale per esporre gli oggetti nativi Dayjs */
  setValue?: (value: [Dayjs | null, Dayjs | null] | null) => void;
  /** Flag esplicito di errore esterno */
  error?: boolean;
  /** Messaggio di aiuto o validazione sotto il campo */
  helperText?: string;
}

export const normalizeDateRange = (val: any): [Dayjs | null, Dayjs | null] => {
  if (!Array.isArray(val)) return [null, null];
  return [normalizeDate(val[0]), normalizeDate(val[1])];
};

dayjs.locale('it');

export const UniversalDateRangePicker = ({
  input,
  meta,
  name,
  value,
  onChange,
  placeholder = 'Seleziona periodo',
  disabled = false,
  noFuture = false,
  disableOpenPicker = false,
  minWidth = 280, // Leggermente più largo per ospitare due date (DD/MM/YYYY - DD/MM/YYYY)
  setValue,
  error,
  helperText,
  ...rest
}: UniversalDateRangePickerProps) => {
  // Estrazione polimorfica dello stato dell'architettura del form o dei componenti controllati standard
  const resolvedName = input?.name || name;
  const resolvedValue = input ? input.value : value;
  const resolvedOnChange = input ? input.onChange : onChange;

  // Consolidamento degli stati di errore di validazione
  const isError = !!(meta?.touched && meta?.error) || !!error;
  const resolvedHelperText = meta?.touched ? meta?.error : helperText || '';

  const handleRangeChange = (newRange: [Dayjs | null, Dayjs | null]) => {
    const start = newRange[0];
    const end = newRange[1];

    const startValid = start && start.isValid();
    const endValid = end && end.isValid();

    // Formatta in stringhe piatte solo se i rispettivi punti sono validi
    const formattedStart = startValid ? start.format('YYYY-MM-DD') : null;
    const formattedEnd = endValid ? end.format('YYYY-MM-DD') : null;

    const payload: [string | null, string | null] = [formattedStart, formattedEnd];

    if (resolvedOnChange) {
      resolvedOnChange(payload);
    }
    if (setValue) {
      setValue(newRange);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <DesktopDateRangePicker
        value={normalizeDateRange(resolvedValue)}
        disableFuture={noFuture}
        disabled={disabled}
        disableOpenPicker={disableOpenPicker}
        onChange={handleRangeChange}
        slots={{
          field: SingleInputDateRangeField
        }}
        slotProps={{
          textField: {
            ...rest,
            name: resolvedName,
            size: 'small',
            placeholder: placeholder,
            error: isError,
            helperText: resolvedHelperText,
            fullWidth: true,
            InputLabelProps: { shrink: true },

            InputProps: !disableOpenPicker
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled={disabled} edge="end" size="small">
                        <CalendarMonthIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              : undefined,

            sx: {
              minWidth: minWidth,
              width: minWidth,

              '& .MuiInputLabel-root': { display: 'none' },
              '& .MuiOutlinedInput-notchedOutline': { legend: { width: 0 } },

              '& .MuiInputBase-root': {
                height: '40px',
                paddingRight: '8px'
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' }
                }
              }
            }
          }
        }}
      />
    </LocalizationProvider>
  );
};
